import React, { useEffect, lazy, Suspense } from "react";
import "./App.css";
import "./styles/home-sections.css";
import "./styles/course-prompt.css";
import "./styles/course-cta.css";
import { useApplyDisplayPreferences } from "./hooks/useDisplayPreferences";
import "./styles/word-search.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
// Per-feature CSS stays eager (small, and keeps lazy routes free of FOUC).
import "./styles/share-sheet.css";
import "./styles/content-pages.css";
import "./styles/site-search.css";
import "./styles/sotd-card.css";
import "./styles/dofe.css";
import { AUDIENCE_PAGES } from "./data/audiencePages";

// Eager: the shell (used on every page) + the home-page sections. Home is
// the default, most-common landing, so we don't want it gated on a chunk.
import Header from "./components/Header";
import Footer from "./components/Footer";
import Seo from "./components/Seo";
import CookieConsent from "./components/CookieConsent";
import NotFound from "./components/NotFound";
import Hero from "./components/Hero";
import HeroExitRamp from "./components/HeroExitRamp";

// Lazy: every other route. This shrinks the home / common first-load JS by
// splitting out dictionary, quizzes, makers and the PDF/canvas generators
// (jspdf, gif.js) into per-route chunks.
// Below-the-fold home sections: lazy so they leave the main bundle and defer
// their work (lower TBT). Hero + HeroExitRamp stay eager (above the fold / LCP).
// The prerender waits for these chunks so the static HTML keeps their content
// (served to crawlers + painted fast); React then client-renders over it via
// createRoot — see src/index.js for why we don't hydrate.
const BroadToolsGrid = lazy(() => import("./components/BroadToolsGrid"));
const SignOfDay = lazy(() => import("./components/SignOfDay"));
const HomeExploreMore = lazy(() => import("./components/HomeExploreMore"));
const CoursePreview = lazy(() => import("./components/CoursePreview"));
const Testimonials = lazy(() => import("./components/Testimonials"));
const HomeFaq = lazy(() => import("./components/HomeFaq"));
const FinalCta = lazy(() => import("./components/FinalCta"));

const Enrol = lazy(() => import("./components/Enrol"));
const SignToMe = lazy(() => import("./components/SignToMe"));
const GiftCoursePage = lazy(() => import("./components/GiftCoursePage"));
const Contact = lazy(() => import("./components/Contact"));
const WhatIsBsl = lazy(() => import("./components/WhatIsBsl"));
const WhereToLearnBsl = lazy(() => import("./components/WhereToLearnBsl"));
const LeftHandedSigning = lazy(() => import("./components/LeftHandedSigning"));
const FreePrintables = lazy(() => import("./components/FreePrintables"));
const FamilySigns = lazy(() => import("./components/FamilySigns"));
const GreetingSigns = lazy(() => import("./components/GreetingSigns"));
const Groups = lazy(() => import("./components/Groups"));
const LessonPlans = lazy(() => import("./components/LessonPlans"));
const LessonPlanDontTalk = lazy(() => import("./components/LessonPlanDontTalk"));
const LessonPlanThroughPlay = lazy(() => import("./components/LessonPlanThroughPlay"));
const StyleReference = lazy(() => import("./components/StyleReference"));
const GamesPackPrint = lazy(() => import("./components/GamesPackPrint"));
const SisterSites = lazy(() => import("./components/SisterSites"));
const BslBooksPage = lazy(() => import("./components/BslBooksPage"));
const BslCollectionPage = lazy(() => import("./components/BslCollectionPage"));
const CarbonFootprint = lazy(() => import("./components/CarbonFootprint"));
const PrivacyPolicy = lazy(() => import("./components/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./components/TermsConditions"));
const RefundPolicy = lazy(() => import("./components/RefundPolicy"));
const CustomerReviews = lazy(() => import("./components/CustomerReviews"));
const CookiesPolicy = lazy(() => import("./components/CookiesPolicy"));
const DataProcessingTerms = lazy(() => import("./components/DataProcessingTerms"));
const FingerspellPage = lazy(() => import("./components/FingerspellPage"));
const AudiencePage = lazy(() => import("./components/AudiencePage"));
const FingerspellPrintPage = lazy(() => import("./components/FingerspellPrintPage"));
const FingerspellQuizPage = lazy(() => import("./components/FingerspellQuizPage"));
const FingerspellingInvadersPage = lazy(() => import("./components/FingerspellingInvadersPage"));
const FingerspellMazePage = lazy(() => import("./components/FingerspellMazePage"));
const LeaderboardPage = lazy(() => import("./components/LeaderboardPage"));
const SproutPage = lazy(() => import("./components/SproutPage"));
const WordSearchMakerPage = lazy(() => import("./components/WordSearchMakerPage"));
const VocabSheetMakerPage = lazy(() => import("./components/VocabSheetMakerPage"));
const GreetingsCardCreatorPage = lazy(() => import("./components/GreetingsCardCreatorPage"));
const WhatsNewPage = lazy(() => import("./components/WhatsNewPage"));
const TeachPage = lazy(() => import("./components/TeachPage"));
const AlphabetChartPage = lazy(() => import("./components/AlphabetChartPage"));
const DailyPage = lazy(() => import("./components/DailyPage"));
const DailySignGuessPage = lazy(() => import("./components/DailySignGuessPage"));
const FingerspellFiveArchivePage = lazy(() => import("./components/FingerspellFiveArchivePage"));
const SignMemoryPage = lazy(() => import("./components/SignMemoryPage"));
const AccessibilityPage = lazy(() => import("./components/AccessibilityPage"));
const StartHerePage = lazy(() => import("./components/StartHerePage"));
const DeafCommunityPage = lazy(() => import("./components/DeafCommunityPage"));
const DofePage = lazy(() => import("./components/DofePage"));
const DictionaryPage = lazy(() => import("./components/DictionaryPage"));
const DictionaryEntryPage = lazy(() => import("./components/DictionaryEntryPage"));
const SignQuizPage = lazy(() => import("./components/SignQuizPage"));
const AdminHubPage = lazy(() => import("./components/AdminHubPage"));
const AdminSotdPage = lazy(() => import("./components/AdminSotdPage"));
const AdminOgPage = lazy(() => import("./components/AdminOgPage"));
const AdminFacebookPage = lazy(() => import("./components/AdminFacebookPage"));
const AdminScoreCardsPage = lazy(() => import("./components/AdminScoreCardsPage"));
const SignOfDayPage = lazy(() => import("./components/SignOfDayPage"));
const EmbedSotdPage = lazy(() => import("./components/EmbedSotdPage"));
const EmbedsIndexPage = lazy(() => import("./components/EmbedsIndexPage"));
const EmbedFingerspellPage = lazy(() =>
  import("./components/EmbedsIndexPage").then((m) => ({ default: m.EmbedFingerspellPage }))
);
const EmbedWordSearchPage = lazy(() =>
  import("./components/EmbedsIndexPage").then((m) => ({ default: m.EmbedWordSearchPage }))
);
const EmbedQuizPage = lazy(() =>
  import("./components/EmbedsIndexPage").then((m) => ({ default: m.EmbedQuizPage }))
);
const EmbedFingerspellPlayerPage = lazy(() =>
  import("./components/EmbedToolPages").then((m) => ({ default: m.EmbedFingerspellPlayerPage }))
);
const EmbedFingerspellPrintPage = lazy(() =>
  import("./components/EmbedToolPages").then((m) => ({ default: m.EmbedFingerspellPrintPage }))
);
const EmbedFingerspellQuizPage = lazy(() =>
  import("./components/EmbedToolPages").then((m) => ({ default: m.EmbedFingerspellQuizPage }))
);

const Home = () => (
  <div className="bs-app">
    <Seo
      title="British Sign Language for Beginners | Free Resources & Online Course"
      description="Learn British Sign Language with our free A–Z BSL dictionary, daily signs and printable resources for schools and families, plus a CPD-certified online course."
      path="/"
      image="/images/og/home.png"
    />
    <Header />
    <main>
      <Hero />
      <HeroExitRamp />
      <Suspense fallback={<div style={{ minHeight: "100vh" }} aria-hidden="true" />}>
        <BroadToolsGrid />
        <SignOfDay />
        <HomeExploreMore />
        <CoursePreview />
        <Testimonials />
        <HomeFaq />
        <FinalCta />
      </Suspense>
    </main>
    <Footer />
  </div>
);

// Scroll to a hash anchor after the route has mounted (browsers' native
// hash-scroll fires before React mounts the target element on SPA navigation).
const ScrollToHash = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, left: 0 });
      return;
    }
    const id = hash.slice(1);
    // Try a few times to handle late-mounting sections.
    let attempts = 0;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (attempts++ < 20) setTimeout(tryScroll, 50);
    };
    tryScroll();
  }, [pathname, hash]);
  return null;
};

function App() {
  /* Apply global accessibility preferences (text scale, high contrast,
     reduced motion) to <html> on every render where the prefs change.
     Lives at the App root so the styling reflects on every page,
     including pages that don't render the DisplayPreferences panel. */
  useApplyDisplayPreferences();
  return (
    <BrowserRouter>
      <ScrollToHash />
      <Suspense fallback={<div style={{ minHeight: "60vh" }} aria-busy="true" />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/enrol" element={<Enrol />} />
        <Route path="/sign-to-me" element={<SignToMe />} />
        <Route path="/gift-a-bsl-course" element={<GiftCoursePage />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/what-is-bsl" element={<WhatIsBsl />} />
        <Route path="/whats-new" element={<WhatsNewPage />} />
        <Route path="/teach" element={<TeachPage />} />
        <Route path="/where-can-i-learn-bsl" element={<WhereToLearnBsl />} />
        <Route path="/left-handed-signing" element={<LeftHandedSigning />} />
        <Route path="/free-resources" element={<FreePrintables />} />
        {/* Renamed from /free-printables - the hub outgrew "printables" (it now
            holds games, tools and daily activities). Real 301 lives in
            .htaccess; this is the client-side backstop. */}
        <Route path="/free-printables" element={<Navigate to="/free-resources" replace />} />
        <Route path="/bsl-family-signs" element={<FamilySigns />} />
        <Route path="/bsl-greetings-signs" element={<GreetingSigns />} />
        <Route path="/groups" element={<Groups />} />
        <Route path="/lesson-plans" element={<LessonPlans />} />
        <Route path="/lesson-plan-dont-talk" element={<Navigate to="/lesson-plan-say-it-without-speaking" replace />} />
        <Route path="/lesson-plan-say-it-without-speaking" element={<LessonPlanDontTalk />} />
        <Route path="/lesson-plan-fingerspelling-through-play" element={<LessonPlanThroughPlay />} />
        <Route path="/sister-sites" element={<SisterSites />} />
        <Route path="/bsl-books" element={<BslBooksPage />} />
        <Route path="/bsl-books/learning" element={<BslCollectionPage slug="learning" />} />
        <Route path="/bsl-books/deaf-culture" element={<BslCollectionPage slug="deaf-culture" />} />
        <Route path="/bsl-books/children" element={<BslCollectionPage slug="children" />} />
        <Route path="/bsl-books/for-schools" element={<BslCollectionPage slug="for-schools" />} />
        <Route path="/carbon-footprint" element={<CarbonFootprint />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-conditions" element={<TermsConditions />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/customer-reviews" element={<CustomerReviews />} />
        <Route path="/cookies-policy" element={<CookiesPolicy />} />
        <Route path="/data-processing-terms" element={<DataProcessingTerms />} />
        <Route path="/fingerspell" element={<FingerspellPage />} />
        <Route path="/print-a-word-in-fingerspelling" element={<FingerspellPrintPage />} />
        <Route path="/fingerspell/quiz" element={<FingerspellQuizPage />} />
        <Route path="/fingerspelling-invaders" element={<FingerspellingInvadersPage />} />
        <Route path="/fingerspell-maze" element={<FingerspellMazePage />} />
        <Route path="/fingerspell/leaderboard" element={<LeaderboardPage />} />
        <Route path="/signspell" element={<SproutPage />} />
        <Route path="/sprout" element={<Navigate to="/signspell" replace />} />
        <Route path="/fingerspelling-word-search-maker" element={<WordSearchMakerPage />} />
        <Route path="/bsl-vocabulary-sheet-creator" element={<VocabSheetMakerPage />} />
        <Route path="/greetings-card-creator" element={<GreetingsCardCreatorPage />} />
        <Route path="/bsl-fingerspelling-alphabet-chart" element={<AlphabetChartPage />} />
        <Route path="/daily" element={<DailyPage />} />
        <Route path="/daily-bsl-word-guess" element={<DailySignGuessPage />} />
        <Route path="/daily-bsl-word-guess/archive" element={<FingerspellFiveArchivePage />} />
        <Route path="/daily-sign-guess" element={<Navigate to="/daily-bsl-word-guess" replace />} />
        <Route path="/sign-memory" element={<SignMemoryPage />} />
        <Route path="/sign-pairs" element={<Navigate to="/sign-memory" replace />} />
        <Route path="/start-here" element={<StartHerePage />} />
        <Route path="/deaf-community" element={<DeafCommunityPage />} />
        <Route path="/dofe" element={<DofePage />} />
        {/* Legacy WP URL /bsl-for-your-dofe-award -> /dofe. Real 301 is in
            .htaccess step 4; this is the client-side backstop. */}
        <Route path="/bsl-for-your-dofe-award" element={<Navigate to="/dofe" replace />} />
        <Route path="/accessibility" element={<AccessibilityPage />} />
        <Route path="/accessibility-statement" element={<Navigate to="/accessibility" replace />} />
        <Route path="/bsl-dictionary" element={<DictionaryPage />} />
        <Route path="/bsl-dictionary/:slug" element={<DictionaryEntryPage />} />
        <Route path="/bsl-sign-quiz" element={<SignQuizPage />} />
        <Route path="/sign-of-the-day" element={<SignOfDayPage />} />
        <Route path="/sign-of-the-day/embed" element={<EmbedSotdPage />} />
        <Route path="/sign-of-the-day/:date" element={<SignOfDayPage />} />
        <Route path="/embed" element={<EmbedsIndexPage />} />
        <Route path="/embed/fingerspell" element={<EmbedFingerspellPage />} />
        <Route path="/embed/wordsearch" element={<EmbedWordSearchPage />} />
        <Route path="/embed/quiz" element={<EmbedQuizPage />} />
        <Route path="/embed/fingerspell-player" element={<EmbedFingerspellPlayerPage />} />
        <Route path="/embed/fingerspell-print" element={<EmbedFingerspellPrintPage />} />
        <Route path="/embed/fingerspell-quiz" element={<EmbedFingerspellQuizPage />} />
        <Route path="/admin" element={<AdminHubPage />} />
        <Route path="/admin/sotd" element={<AdminSotdPage />} />
        <Route path="/admin/og" element={<AdminOgPage />} />
        <Route path="/admin/facebook" element={<AdminFacebookPage />} />
        <Route path="/admin/score-cards" element={<AdminScoreCardsPage />} />
        <Route path="/british-sign-language-dictionary" element={<Navigate to="/bsl-dictionary" replace />} />
        <Route path="/for-schools" element={<AudiencePage config={AUDIENCE_PAGES.schools} />} />
        <Route path="/for-libraries" element={<AudiencePage config={AUDIENCE_PAGES.libraries} />} />
        <Route path="/for-tutors" element={<AudiencePage config={AUDIENCE_PAGES.tutors} />} />
        <Route path="/for-scouts-guides" element={<AudiencePage config={AUDIENCE_PAGES.scouts} />} />
        <Route path="/style-reference" element={<StyleReference />} />
        <Route path="/print/games-pack" element={<GamesPackPrint />} />
        <Route path="/print/games-pack/:slug" element={<GamesPackPrint />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
      <CookieConsent />
    </BrowserRouter>
  );
}

export default App;
